"use client";

import { useCallback, useId, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useFieldArray, type UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input, ContainerInput, Textarea } from "@/components/ui/input";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { FilterSectionsContent } from "@/features/search/components/filters";
import { useOptionalTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { Icon } from "@/components/ui/icon";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { EmptyPresetsIllustration } from "@/components/illustrations/empty-presets-illustration";
import { logo } from "@/lib/logo-dev";
import type { AutocompleteResult } from "@/lib/api/types";
import type { CsvImportResult } from "@/stores/csv-import-store";
import {
  getPresetMeta,
  type PresetTypeMeta,
  type PresetTypeSlug,
} from "../lib/categories";
import { buildDefaultStagePresetStages } from "../lib/stage-preset";
import { ImportPresetCsv } from "./import-csv";
import { IconButton, FOCUS_RING } from "./icon-button";
import { VisibilityInput } from "./visibility-input";
import { PresetTypeInput } from "./preset-type-input";
import { StagePresetEditor } from "./stage-preset-editor";
import type { EntityItem, PresetFormValues } from "./schema";

const NOOP = () => {};
const LOGO_TYPES: PresetTypeSlug[] = ["companies", "universities", "investors"];

interface PresetFormFieldsProps {
  form: UseFormReturn<PresetFormValues>;
  /** Hide the Preset Type selector in edit mode. */
  isEdit: boolean;
}

export function PresetFormFields({ form, isEdit }: PresetFormFieldsProps) {
  const presetType = form.watch("presetType");
  const meta = getPresetMeta(presetType);
  const isFilterType = presetType === "filters";
  const isJobStageType = presetType === "job-stages";

  const datePlaceholder = useMemo(() => {
    const now = new Date();
    return `${now.toLocaleString("en-US", { month: "short" })} ${now.getDate()}, ${now.getFullYear()}`;
  }, []);

  const titlePlaceholder = useMemo(() => {
    const typeLabel = presetType === "universities" ? "Schools" : meta.label;
    return `New ${typeLabel} Preset (${datePlaceholder})`;
  }, [presetType, meta.label, datePlaceholder]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5">
      {!isEdit && (
        <FormField
          control={form.control}
          name="presetType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Preset Type</FormLabel>
              <FormControl>
                <PresetTypeInput
                  value={field.value}
                  onChange={(next) => {
                    field.onChange(next);
                    form.setValue("entities", [], { shouldValidate: true });
                    if (next === "job-stages") {
                      form.setValue(
                        "stages",
                        buildDefaultStagePresetStages(),
                        { shouldValidate: true },
                      );
                    }
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Preset Title</FormLabel>
            <FormControl>
              <Input
                variant="default"
                placeholder={titlePlaceholder}
                required
                aria-required="true"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel optional>Preset Description</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Describe what this preset is about"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="visibility"
        render={({ field }) => {
          const labelId = `visibility-label-${field.name}`;
          return (
            <FormItem>
              <FormLabel id={labelId} optional>
                Preset Visibility
              </FormLabel>
              <FormControl>
                <VisibilityInput
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  aria-labelledby={labelId}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      {!isFilterType && !isJobStageType && (
        <div
          aria-hidden="true"
          className="w-full h-px bg-contrast/10 shrink-0"
        />
      )}

      {isFilterType ? (
        <FilterPresetSection />
      ) : isJobStageType ? (
        <StagePresetEditor form={form} />
      ) : (
        <EntityListSection form={form} meta={meta} presetType={presetType} />
      )}
    </div>
  );
}

interface EntityListSectionProps {
  form: UseFormReturn<PresetFormValues>;
  meta: PresetTypeMeta;
  presetType: PresetTypeSlug;
}

function EntityListSection({ form, meta, presetType }: EntityListSectionProps) {
  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "entities",
    keyName: "fieldId",
  });

  const [csvOpen, setCsvOpen] = useState(false);
  const searchLabelId = useId();

  const selectedValues = useMemo(() => fields.map((e) => e.name), [fields]);

  const appendEntity = useCallback(
    (item: EntityItem) => {
      if (
        fields.some((e) => e.name.toLowerCase() === item.name.toLowerCase())
      ) {
        return;
      }
      append(item, { shouldFocus: false });
    },
    [append, fields],
  );

  const handleAutocompleteSelect = useCallback(
    (result: AutocompleteResult) => {
      appendEntity({
        id: crypto.randomUUID(),
        name: result.value,
        domain: result.domain ?? undefined,
      });
    },
    [appendEntity],
  );

  const handleFreeText = useCallback(
    (text: string) => {
      appendEntity({
        id: crypto.randomUUID(),
        name: text,
      });
    },
    [appendEntity],
  );

  const handleCsvImport = useCallback(
    (imported: CsvImportResult[]) => {
      for (const item of imported) {
        appendEntity({
          id: crypto.randomUUID(),
          name: item.name,
          domain: item.domain,
        });
      }
    },
    [appendEntity],
  );

  return (
    <FormField
      control={form.control}
      name="entities"
      render={() => (
        <FormItem className="flex flex-col gap-2 w-full">
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-1">
              <FormLabel id={searchLabelId}>{meta.searchLabel}</FormLabel>
              <Icon
                name="IconCircleInfo"
                size={14}
                aria-hidden="true"
                className="text-heading opacity-30"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                size="text"
                variant="muted-ghost"
                onClick={() => replace([])}
              >
                Clear All
              </Button>
            </div>
          </div>

          <div className="shrink-0 flex gap-2">
            <AutocompleteInput
              field={meta.autocompleteField}
              placeholder={meta.searchPlaceholder}
              allowFreeText={meta.allowFreeText}
              selectedValues={selectedValues}
              onSelect={handleAutocompleteSelect}
              onFreeText={handleFreeText}
            />
            <Button
              type="button"
              onClick={() => setCsvOpen(true)}
              variant="secondary"
            >
              <Icon name="IconNewspaper1" size={14} aria-hidden="true" />
              <span>Import from CSV</span>
            </Button>
          </div>

          <ImportPresetCsv
            open={csvOpen}
            onClose={() => setCsvOpen(false)}
            onImport={handleCsvImport}
            presetType={presetType}
          />

          <div className="min-h-[20rem] bg-contrast/[0.02] border border-contrast/10 rounded-lg p-3 overflow-y-auto">
            {fields.length > 0 ? (
              <div className="flex flex-wrap gap-2 content-start">
                {fields.map((entity, index) => (
                  <EntityTag
                    key={entity.fieldId}
                    entity={entity}
                    onRemove={() => remove(index)}
                    presetType={presetType}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <EmptyPresetsIllustration
                  height={100}
                  width={150}
                  className="w-[150px] h-[100px] opacity-70"
                />
                <div className="flex flex-col items-center gap-1">
                  <p className="text-base font-medium text-heading">
                    No {meta.entityNoun} added yet
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Use the search / upload options above to add some.
                  </p>
                </div>
              </div>
            )}
          </div>

          <FormDescription className="sr-only">
            {`Add the ${meta.entityNoun} that should be part of this preset.`}
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function EntityTag({
  entity,
  onRemove,
  presetType,
}: {
  entity: EntityItem;
  onRemove: () => void;
  presetType: PresetTypeSlug;
}) {
  const logoUrl = LOGO_TYPES.includes(presetType)
    ? logo({ domain: entity.domain, name: entity.name, size: 32 })
    : null;

  return (
    <span className="relative inline-flex items-center gap-1.5 h-7 px-1.5 rounded-md bg-contrast/4 border border-contrast/5 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
      {logoUrl ? (
        <IconAvatar
          src={logoUrl}
          size={16}
          alt={entity.name}
          className="shrink-0 rounded-[3px]"
          icon={<Icon name="IconBag" className="text-heading opacity-50" />}
        />
      ) : null}
      <span className="text-sm font-medium text-contrast/85">
        {entity.name}
      </span>
      <button
        type="button"
        aria-label={`Remove ${entity.name}`}
        onClick={onRemove}
        className={`relative flex items-center justify-center size-3.5 rounded text-contrast/30 hover:text-contrast/60 transition-colors cursor-pointer before:absolute before:left-1/2 before:top-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] ${FOCUS_RING}`}
      >
        <Icon name="IconCrossMedium" size={10} aria-hidden="true" />
      </button>
      <span
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
      />
    </span>
  );
}

function FilterPresetSection() {
  const talentSearchFilters = useOptionalTalentSearchFilters();
  const clearFilters = talentSearchFilters?.clearFilters ?? NOOP;

  return (
    <div className="flex flex-col w-full gap-3">
      <div aria-hidden="true" className="w-full h-px bg-contrast/10 shrink-0" />
      <div
        className="min-h-[20rem] border border-contrast/10 rounded-lg overflow-hidden p-2.5"
        style={{
          backgroundImage:
            "radial-gradient(circle, var(--border-subtle-alpha) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      >
        <div className="h-full overflow-y-auto rounded-md border border-contrast/5 bg-surface-primary">
          <FilterSectionsContent />
        </div>
      </div>
      <div className="shrink-0">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => clearFilters()}
          className="relative h-7 min-h-7 w-full border border-contrast/30 bg-transparent shadow-none hover:border-contrast/50 hover:bg-transparent active:bg-transparent"
        >
          {/* TODO(a11y AAA-contrast): text-contrast/60 may not reach 7:1 on dark surfaces. */}
          <span className="text-sm font-medium text-contrast/60 leading-5">
            Clear all filters
          </span>
        </Button>
      </div>
    </div>
  );
}
